/**
 * Date/timestamp conversion utilities.
 *
 * Strategy: all dates are transported between frontend and backend as
 * Unix timestamps (milliseconds since epoch) created at **UTC** midnight.
 * This avoids timezone conversion pitfalls that occur when passing
 * "YYYY-MM-DD" strings (which get interpreted as midnight UTC by the
 * database, shifting the displayed date when viewed in a different
 * timezone).
 *
 * • Frontend picks a date → Date.UTC(y, m-1, d) → sends number to backend
 * • Backend receives timestamp → UTC methods → stores "YYYY-MM-DD"
 * • Database returns "YYYY-MM-DD" → Date.UTC(y, m-1, d) → number
 * • Frontend receives number → UTC methods → displays "YYYY-MM-DD"
 *
 * The key invariant: dateToTimestamp(timestampToDate(ts)) === ts
 * for any UTC-midnight timestamp.
 */

/**
 * Convert a YYYY-MM-DD date string to a Unix timestamp (ms).
 * Creates the timestamp at **UTC** midnight for the given date.
 */
export function dateToTimestamp(dateStr: string | number | undefined | null): number | undefined {
  if (!dateStr || dateStr === '') return undefined;
  if (typeof dateStr === 'number') return dateStr;

  const s = String(dateStr);
  // If it looks like a timestamp number (may be negative for pre-1970), pass through
  if (/^-?\d+$/.test(s)) return Number(s);

  // Parse as UTC midnight: "2026-06-17" → 2026-06-17T00:00:00Z
  const [y, m, d] = s.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/**
 * Convert a Unix timestamp back to a YYYY-MM-DD date string.
 * Uses **UTC** to extract the date components.
 */
export function timestampToDate(ts: string | number | undefined | null): string | undefined {
  if (ts === undefined || ts === null || ts === '') return undefined;

  const num = typeof ts === 'number' ? ts : Number(ts);
  if (isNaN(num)) return undefined;

  const date = new Date(num);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Convert login_allowed (tinyint 0/1) to a boolean.
 */
export function boolFromTimestamp(value: boolean | number | undefined | null): boolean {
  if (value === undefined || value === null) return false;
  return !!value;
}
